/**
 * Profile hooks (React Query)
 *
 * Server-state access for the signed-in user's own homeowner / designer /
 * contractor profile. Reads come from the owning services; writes invalidate
 * both the profile query and the current-user query so the app shell (which
 * reads `user`) reflects saved changes.
 */

import {
	type QueryKey,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	fetchDesignerProfile,
	getContractorProfile,
} from "@/features/marketplace/services/marketplace-api.service";
import {
	getMyRealtorProfile,
	updateMyRealtorProfile,
} from "@/features/realtor/services/realtor-api.service";
import {
	getHomeownerProfile,
	updateContractorProfile,
	updateDesignerProfile,
	updateHomeownerProfile,
} from "@/features/settings/services/settings-api.service";
import { queryKeys } from "@/lib/query-keys";

/** `enabled` lets the app shell skip the read for other user types. */
export function useHomeownerProfile(enabled = true) {
	return useQuery({
		queryKey: queryKeys.settings.homeownerProfile(),
		queryFn: getHomeownerProfile,
		enabled,
	});
}

/**
 * Reads through `fetchDesignerProfile`, which rethrows anything but a 404, so
 * `isError` distinguishes a broken request from a user who has not onboarded.
 * The homeowner and contractor getters still flatten both to `null`.
 */
export function useDesignerProfile() {
	return useQuery({
		queryKey: queryKeys.settings.designerProfile(),
		queryFn: fetchDesignerProfile,
	});
}

/** `enabled` lets the app shell skip the read for other user types. */
export function useContractorProfile(enabled = true) {
	return useQuery({
		queryKey: queryKeys.settings.contractorProfile(),
		queryFn: getContractorProfile,
		enabled,
	});
}

/** `enabled` lets the app shell skip the read for other user types. */
export function useRealtorProfile(enabled = true) {
	return useQuery({
		queryKey: queryKeys.settings.realtorProfile(),
		queryFn: async () => {
			const result = await getMyRealtorProfile();
			return result.profile ?? null;
		},
		enabled,
	});
}

/**
 * Invalidate the profile query that was just written plus the current-user
 * query, so both the edit page and the app shell refetch fresh data.
 */
function useProfileInvalidation(profileKey: QueryKey) {
	const queryClient = useQueryClient();
	return () => {
		queryClient.invalidateQueries({ queryKey: profileKey });
		queryClient.invalidateQueries({ queryKey: queryKeys.user.me() });
	};
}

export function useUpdateHomeownerProfile() {
	const invalidate = useProfileInvalidation(
		queryKeys.settings.homeownerProfile(),
	);
	return useMutation({
		mutationFn: updateHomeownerProfile,
		onSuccess: (result) => {
			if (result.success) invalidate();
		},
	});
}

export function useUpdateDesignerProfile() {
	const invalidate = useProfileInvalidation(
		queryKeys.settings.designerProfile(),
	);
	return useMutation({
		mutationFn: updateDesignerProfile,
		onSuccess: (result) => {
			if (result.success) invalidate();
		},
	});
}

export function useUpdateContractorProfile() {
	const invalidate = useProfileInvalidation(
		queryKeys.settings.contractorProfile(),
	);
	return useMutation({
		mutationFn: updateContractorProfile,
		onSuccess: (result) => {
			if (result.success) invalidate();
		},
	});
}

export function useUpdateRealtorProfile() {
	const invalidate = useProfileInvalidation(
		queryKeys.settings.realtorProfile(),
	);
	return useMutation({
		mutationFn: updateMyRealtorProfile,
		onSuccess: (result) => {
			if (result.success) invalidate();
		},
	});
}
